"use client";

/**
 * Shared left-column visual panel for the auth pages (login,
 * password-recovery, password-reset).
 *
 * Renders only on lg+ (hidden on mobile). Reuses the same data sources
 * as the homepage hero so the stats stay in sync:
 *   - countries / tours counts → `state.countries.countries`
 *   - avg rating → `useReviewsAggregate()` (`/api/reviews-aggregate`)
 *   - testimonial → `state.reviews.randomReviews` filtered to 5★
 */

import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "@/app/lib/hooks";
import { getCountries } from "@/app/lib/reducers/countriesReducer";
import { getRandomReviews } from "@/app/lib/reducers/reviewsReducer";

// Same Paris sunset photo as the design mock (`styles.css :: .gd-img.sunset`).
const AUTH_HERO_IMAGE =
  "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=1600&q=70";

// Lightweight inline mirror of the homepage hero's `useReviewsAggregate`
// hook. Inlining avoids the cross-route-group import path
// (`@/app/[lang]/[currency]/(chrome)/(home)/desktop/...`) which can resolve
// inconsistently in dev — keeping the auth bundle self-contained.
interface ReviewsAggregate {
  avgRating: number;
  totalReviews: number;
}
let cachedAggregate: ReviewsAggregate | null = null;
let inflight: Promise<ReviewsAggregate | null> | null = null;
const fetchAggregate = (): Promise<ReviewsAggregate | null> => {
  if (cachedAggregate) return Promise.resolve(cachedAggregate);
  if (inflight) return inflight;
  const base = process.env.NEXT_PUBLIC_SOCKET || "";
  if (!base) return Promise.resolve(null);
  inflight = fetch(`${base}/api/reviews-aggregate`, {
    headers: { Accept: "application/json" },
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((data: any) => {
      if (!data || typeof data.avgRating !== "number") return null;
      cachedAggregate = {
        avgRating: data.avgRating,
        totalReviews: data.totalReviews ?? 0,
      };
      return cachedAggregate;
    })
    .catch(() => null)
    .finally(() => {
      inflight = null;
    });
  return inflight;
};
const useReviewsAggregate = (enabled: boolean): ReviewsAggregate | null => {
  const [agg, setAgg] = useState<ReviewsAggregate | null>(cachedAggregate);
  useEffect(() => {
    // Meme raison que les deux autres appels : rien a aller chercher pour un
    // panneau que le CSS masque.
    if (!enabled) return undefined;
    if (cachedAggregate) {
      setAgg(cachedAggregate);
      return undefined;
    }
    let aborted = false;
    fetchAggregate().then((data) => {
      if (!aborted && data) setAgg(data);
    });
    return () => {
      aborted = true;
    };
  }, [enabled]);
  return agg;
};

const AuthVisualPanel: FC = () => {
  const { t } = useTranslation("guydeez");
  const dispatch = useAppDispatch();

  const selectedLanguage = useAppSelector(
    (s: any) => s?.language?.selectedLanguage || "fr_FR"
  );
  const countries = useAppSelector(
    (s: any) => s?.countries?.countries ?? []
  );
  const randomReviews = useAppSelector(
    (s: any) => s?.reviews?.randomReviews ?? []
  );

  // Le panneau est `hidden lg:flex` : sous 1024px il est dans le DOM mais
  // jamais affiche. Ses trois appels d'API (pays, avis, note moyenne) partaient
  // quand meme, sur chaque ouverture de /login, /signup ou /password-reset
  // depuis un telephone — trois allers-retours, trois JSON a parser et un
  // sous-arbre React a re-rendre, pour des pixels que personne ne verra. On ne
  // les declenche donc qu'a partir du point de rupture ou le panneau existe.
  //
  // `useState` + listener plutot qu'un test unique : un ecran qu'on tourne, ou
  // une fenetre qu'on agrandit, doit remplir le panneau qui vient d'apparaitre.
  const [isWide, setIsWide] = useState(false);
  useEffect(() => {
    const query = window.matchMedia("(min-width: 1024px)");
    const sync = () => setIsWide(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  const reviewsAggregate = useReviewsAggregate(isWide);

  useEffect(() => {
    if (!isWide) return;
    if (!countries?.length) {
      dispatch(getCountries(null, true, selectedLanguage));
    }
  }, [dispatch, isWide, countries?.length, selectedLanguage]);

  useEffect(() => {
    if (!isWide) return;
    // Refresh when locale changes — the comment text comes back in the
    // user's active language.
    dispatch(getRandomReviews(8, selectedLanguage));
  }, [dispatch, isWide, selectedLanguage]);

  const stats = useMemo(() => {
    const active = countries.filter((c: any) => c?.activated);
    const totalTours = active.reduce(
      (acc: number, c: any) => acc + (c.numTours || 0),
      0
    );
    return {
      countries: active.length,
      tours: totalTours,
    };
  }, [countries]);

  const featuredReview = useMemo(() => {
    if (!Array.isArray(randomReviews) || !randomReviews.length) return null;
    const fiveStar = randomReviews.filter(
      (r: any) => Number(r?.rating) === 5
    );
    return fiveStar[0] || randomReviews[0] || null;
  }, [randomReviews]);

  const locale = selectedLanguage.replace("_", "-");

  return (
    <div
      className="relative overflow-hidden hidden lg:flex"
      style={{
        backgroundImage: `url("${AUTH_HERO_IMAGE}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: "white",
        padding: "70px 60px",
        flexDirection: "column",
        justifyContent: "space-between",
        minHeight: "100%",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background:
            "linear-gradient(135deg, rgba(94,30,30,.75), rgba(94,30,30,.4) 60%, rgba(0,0,0,.4))",
        }}
      />

      <div style={{ position: "relative" }}>
        <div
          style={{
            fontSize: 12,
            color: "var(--g-coral-soft, #f4c9c2)",
            fontWeight: 700,
            letterSpacing: ".12em",
            marginBottom: 14,
          }}
        >
          {t("login_visual_eyebrow", {
            defaultValue: "BIENVENUE CHEZ GUYDEEZ",
          })}
        </div>
        <h2
          style={{
            fontSize: 42,
            fontWeight: 800,
            lineHeight: 1.1,
            marginBottom: 20,
          }}
        >
          {(t("login_visual_headline", {
            defaultValue: "Vos plus belles\nvisites vous attendent.",
          }) as string)
            .split("\n")
            .map((line, i, arr) => (
              <React.Fragment key={i}>
                {line}
                {i < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
        </h2>
        <p
          style={{
            fontSize: 15,
            opacity: 0.9,
            maxWidth: 420,
            lineHeight: 1.5,
          }}
        >
          {t("login_visual_subtitle", {
            defaultValue:
              "Retrouvez vos réservations, vos guides favoris, et bénéficiez de tarifs membres.",
          })}
        </p>
      </div>

      {featuredReview && (
        <div
          style={{
            position: "relative",
            background: "rgba(255,255,255,.10)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,.18)",
            borderRadius: 16,
            padding: 22,
            maxWidth: 460,
            marginTop: 40,
          }}
        >
          <div
            style={{
              fontSize: 14,
              lineHeight: 1.5,
              marginBottom: 14,
              fontStyle: "italic",
            }}
          >
            «{" "}
            {(featuredReview.comment || "").length > 220
              ? (featuredReview.comment || "").slice(0, 220).trimEnd() + "…"
              : featuredReview.comment}{" "}
            »
          </div>
          <div className="flex items-center" style={{ gap: 12 }}>
            <div
              className="grid place-items-center"
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "rgba(255,255,255,.22)",
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              {(featuredReview.name || "?").trim().charAt(0).toUpperCase()}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>
                {featuredReview.name}
                {featuredReview.cityTranslated
                  ? ` · ${featuredReview.cityTranslated}`
                  : ""}
              </div>
              <div style={{ fontSize: 11, opacity: 0.8 }}>
                ★{" "}
                {Number(featuredReview.rating || 5).toLocaleString(locale, {
                  minimumFractionDigits: 1,
                  maximumFractionDigits: 1,
                })}
                {featuredReview.tourTranslated
                  ? ` · ${featuredReview.tourTranslated}`
                  : ""}
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className="flex"
        style={{
          gap: 22,
          fontSize: 12,
          opacity: 0.9,
          marginTop: 40,
          position: "relative",
          flexWrap: "wrap",
        }}
      >
        {stats.tours > 0 && (
          <span>
            <strong style={{ fontSize: 18, fontWeight: 800 }}>
              {stats.tours.toLocaleString(locale)}
            </strong>{" "}
            {t("login_visual_stats_tours", { defaultValue: "tours" })}
          </span>
        )}
        {stats.countries > 0 && (
          <span>
            <strong style={{ fontSize: 18, fontWeight: 800 }}>
              {stats.countries}
            </strong>{" "}
            {t("login_visual_stats_countries", { defaultValue: "pays" })}
          </span>
        )}
        {reviewsAggregate && reviewsAggregate.totalReviews > 0 && (
          <span>
            <strong style={{ fontSize: 18, fontWeight: 800 }}>
              ★{" "}
              {reviewsAggregate.avgRating.toLocaleString(locale, {
                minimumFractionDigits: 1,
                maximumFractionDigits: 1,
              })}
            </strong>{" "}
            {t("login_visual_stats_rating", { defaultValue: "moyenne" })}
          </span>
        )}
      </div>
    </div>
  );
};

export default AuthVisualPanel;
